import {
  Component,
  Input,
  OnDestroy,
  TemplateRef,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
  KeyValueDiffer,
  KeyValueDiffers,
  ContentChildren,
  QueryList,
  ViewChildren,
  ElementRef,
  AfterContentInit,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { MktCarouselItemDirective } from './mkt-carousel3-item.directive';
import { CdsAnnouncerService } from '@citi-gcg-173875/citi-common-ui-cds';

export enum Animation {
  Fade = 'fade',
  Slide = 'slide'
}

export enum Transition {
  Next,
  Prev
}
export interface ActiveSlides {
  previous: number;
  current: number;
  next: number;
}

const TRANSITION_TIME = 1000;

@Component({
  selector: 'mkt-carousel3',
  host: { class: 'mkt-carousel3' },
  templateUrl: './mkt-carousel3.component.html',
  styleUrls: ['./mkt-carousel3.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
   animations: [
    // trigger('slideState', [
    //   state('current', style({
    //     transform: 'translateX(0%)',
    //     zIndex: 1
    //   })),
    //   state('next', style({
    //     transform: 'translateX(100%)',
    //     zIndex: 1
    //   })),
    //   state('previous', style({
    //     transform: 'translateX(-100%)',
    //     zIndex: 1
    //   })),
    //   transition('current => previous', animate(`${TRANSITION_TIME}ms ease-out`)),
    //   transition('next => current', animate(`${TRANSITION_TIME}ms ease-out`)),
    //   transition('current => next', animate(`${TRANSITION_TIME}ms ease-out`)),
    //   transition('previous => current', animate(`${TRANSITION_TIME}ms ease-out`))
    // ])
  ],
})
export class MktCarousel3Component implements OnInit, OnDestroy, AfterContentInit {

/**
 * This keeps track of the index of the previous, current, and next slides
 * @Example: for a 10 slide carousel
 *  {
 *     previous: 9,
 *     current: 0,
 *     next: 1
 *  }
 */
  differ: KeyValueDiffer<ActiveSlides, any>;

  public showPause: boolean = false;

  isActiveSlide: boolean = false;

  public hasFocus: boolean = false;

  private animationComplete = true;

  private currentSlide: MktCarouselItemDirective;

  private carouselSlideContentArray: MktCarouselItemDirective[] = [];

  /**
   * @Property: idStr
   * @Description: id for the carousel
   * @Type: string
  */
  @Input() idStr: string = 'carousel';

  /**
   * @Property: slides
   * @Description: slides for the carousel
   * @Type: Any
  */
  @Input() slides;

  /**
   * @Property: isNavigation
   * @Description: show/hide NEXT/PREV button
   * @Type: Boolean
  */
  @Input() isNavigation = false;

  /**
   * @Property: isControls
   * @Description: show/hide controls
   * @Type: Boolean
  */
  @Input() isControls = false;

  /**
   * @Property: autoPlayDuration
   * @Description: set the auto rotation time
   * @Type: Nubmer
   * @Default: 0
   */
  @Input() autoPlayDuration = 0;

  /**
   * @Property: ariaLabel
   * @Description: This defines the custom value for the aria-label attribute of the Carousel section tag
   * @Type: String
   * @Default:
   */
  @Input() ariaLabel: string = 'Carousel slide';

  /**
   * @Property: prevAriaLabel
   * @Description: This defines the custom value for the aria-label attribute of the previous slide button
   * @Type: String
   * @Default:
  */
  @Input() prevAriaLabel: string = 'Previous Slide';

  /**
   * @Property: nextAriaLabel
   * @Description: This defines the custom value for the aria-label attribute of the next slide button
   * @Type: String
   * @Default:
  */
  @Input() nextAriaLabel: string = 'Next Slide';

  /**
   * @Property: playAriaLabel
   * @Description: This defines the custom value for the aria-label attribute of the play button
   * @Type: String
   * @Default:
  */
  @Input() playAriaLabel: string = 'Play';

  /**
   * @Property: pauseAriaLabel
   * @Description: This defines the custom value for the aria-label attribute of the pause button
   * @Type: String
   * @Default:
  */
  @Input() pauseAriaLabel: string = 'Pause';

  /**
   * @Property: announceSlideChange
   * @Description: This defines the custom string that will be announced via live announcer when the carousel transitions to a new slide
   * @Type: String
   * @Default:
   */
  @Input() set announceSlideChange(message: string) {
    if (this._announceSlideChange === message) return;
    this._announceSlideChange = message;
    this.announceSlide(message);
  }
  private _announceSlideChange: string;

  /**
   * @Property: activeSlide
   * @Description: This defines the index of the active slide that the carousel should display initially
   * @Type: Number
   * @Default: 0
  */
  _activeSlide: number = 0;
  @Input() set activeSlide(activeSlide: number) {
    if (this._activeSlide === activeSlide) return;
    this._activeSlide = activeSlide;
    if (this.carouselSlides && this.carouselSlides.length) { // prevents setting before init
      this.deactiveCurrentSlide();
      this.activeNewSlide(activeSlide);
    }
  }

  private _activeSlides: ActiveSlides;
  get activeSlides() {
    return this._activeSlides;
  }
  set activeSlides(activeSlides: ActiveSlides) {
    if (this._activeSlides === activeSlides) return;
    this._activeSlides = activeSlides;
  }

  @Input() tabIndex: number;

  @Input() slideTemplateRef: TemplateRef<any>;

  @Input() thumbnailTemplateRef: TemplateRef<any>;

  @Output() activeSlideChange: EventEmitter<number> = new EventEmitter()

  @Input('slideRefs') carouselSlideRefs: QueryList<ElementRef>;

  @ContentChildren(MktCarouselItemDirective) carouselSlides: QueryList<MktCarouselItemDirective>;

  @ViewChild('carousel') carouselRef: ElementRef<HTMLElement>;

  @ViewChildren('slide') slideRefs: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private cd: ChangeDetectorRef,
    private differs: KeyValueDiffers,
    private announcer: CdsAnnouncerService,
  ) { 
    cd.detach();
    setInterval(() => {
      this.cd.detectChanges()
    }, TRANSITION_TIME)
  }

  ngOnInit(): void {

    if (this.slides) {
      this.activeSlides = this.getPreviousCurrentNextIndexes(0);
      this.differ = this.differs.find(this.activeSlides).create();
    }
  }

  ngAfterContentInit() {
    setTimeout(() => {
      this.carouselSlideContentArray = this.carouselSlides.toArray();
      this.currentSlide = this.carouselSlideContentArray[this._activeSlide];
      this.currentSlide.isActiveSlide = true;
    }, 1);
  }

  ngOnDestroy(): void {
    this.cd.detach();
  }

  select(index: number) {
    // prevent user from clicking too fast
    if (!this.animationComplete) return;
    
    this.animationComplete = false;
    this.activeSlides = this.getPreviousCurrentNextIndexes(index);
    this.activeSlideChange.emit(index);
    if (this.differ.diff(this.activeSlides)) {
      this.cd.detectChanges();
    }

    // (<HTMLElement>document.activeElement).blur(); // iOS fix
    this.focusElement(this.carouselRef.nativeElement);

    setTimeout(() => this.animationEnd(), TRANSITION_TIME);
  }

  getPreviousCurrentNextIndexes(index: number): ActiveSlides {
    const images = this.slides;

    return {
      previous: (index === 0 ? images.length - 1 : index - 1) % images.length,
      current: index % images.length,
      next: (index === images.length - 1 ? 0 : index + 1) % images.length
    };
  }

  getAnimationSlideState(index: number) {
    let state = 'next';
    if (index < this.activeSlides.current) state = 'previous';
    else if (index === this.activeSlides.current) state = 'current';
    else if (index > this.activeSlides.current) state = 'next';
    return state;
  }

  onCarouselFocus() {
    this.announceSlide(this._announceSlideChange);
  }

  activeNewSlide(index: number) {
    this._activeSlide = index;
    this.activeSlideChange.emit(index);
    if (this.carouselSlideContentArray.length) {
      this.currentSlide = this.carouselSlideContentArray[index];
      this.currentSlide.isActiveSlide = true;
    }
  }

  announceSlide(message: string) {
    this.announcer.announce(`Now Displaying slide ${this.activeSlides.current + 1}
    out of ${this.carouselSlideContentArray.length}. ${message}`);
  }

  deactiveCurrentSlide() {
    this.currentSlide.isActiveSlide = false;
  }

  // previouse and next slide display none after animation is done.
  animationEnd() {
    this.animationComplete = true;

    // Only run if slides exist
    if (this.slideRefs && this.slideRefs.toArray().length > 0) {
      // Get current slide from list of slides
      let currentSlide = this.slideRefs.toArray()[this.activeSlides.current];

      // Confirm nativeElement exist
      if (currentSlide && currentSlide.nativeElement) {
        // Element with 'ng-animating' class name
        let elem = <HTMLElement>currentSlide.nativeElement;

        // // Delay start for approximate animation end
        // setTimeout(() => {
          // Set interval to wait for end of animation
          // let waitForAnimation = setInterval(() => {
            // Check animation progress
            if (elem.classList.contains('ng-animating')) return;

            // Clear interval
            // clearInterval(waitForAnimation);

            // Focus heading if it exists, otherwise focus the slide container
            let selectors = ['.slide-title', 'h2', '.header-2', '.header-level-2', 'h3', '.header-3', '.header-level-3', 'h4', '.header-4', '.header-level-4'];
            let heading = <HTMLElement>elem.querySelector(selectors.join(','));
            this.focusElement(heading || elem);
          // }, 200);
        // }, 1000);
      }
    }
  }

  focusElement(elem) {
    // Prepare for focus event
    if (!elem.getAttribute('tabindex')) {
      elem.setAttribute("tabindex", "-1");
      // elem.setAttribute("aria-live", "assertive");
    }

    // Set focus to element
    // elem.blur(); // iOS fix
    // setTimeout(() => {
      elem.focus({ preventScroll: true });
    // }, 50);

    // Remove focus to element
    // setTimeout(() => {
    //   originals['aria-live'] ? elem.setAttribute("aria-live", originals['aria-live']) : elem.removeAttribute("aria-live");
    //   originals['tabindex'] ? elem.setAttribute("tabindex", originals['tabindex']) : elem.removeAttribute("tabindex");
    // }, 50);
  }
};
